import { apiClient } from './apiClient'
import {
  InviteSocietyMembersResponse,
  SocietyInvitation,
  SocietyInviteeName,
  SocietyJoinCode,
  SocietyMember,
  UserSocietyMembership
} from '../types/dtos'

export interface SocietyJoinCodePreview {
  societyName: string
}

export interface SocietyMemberSearchResult {
  id: number
  firstName: string
  lastName: string
  gender?: string
  dateOfBirth?: string
  handicapIndex?: string | number
  email: string
  homeClubName?: string
  homeClubAbbreviation?: string
}

class SocietyService {
  getMembers = async (tenantId: string): Promise<SocietyMember[]> => {
    const response = await apiClient.get<SocietyMember[]>(`/societies/${tenantId}/members`)
    return response.data
  }

  searchMembers = async (tenantId: string, query: string): Promise<SocietyMemberSearchResult[]> => {
    const response = await apiClient.get<SocietyMemberSearchResult[]>(`/societies/${tenantId}/members/search`, { params: { query } })
    return response.data
  }

  getActiveMembers = async (tenantId: string): Promise<SocietyMemberSearchResult[]> => {
    const response = await apiClient.get<SocietyMemberSearchResult[]>(`/societies/${tenantId}/members/active`)
    return response.data
  }

  inviteMembers = async (tenantId: string, emails: string[], names: SocietyInviteeName[] = []): Promise<InviteSocietyMembersResponse> => {
    const response = await apiClient.post<InviteSocietyMembersResponse>(`/societies/${tenantId}/members`, { emails, names })
    return response.data
  }

  deleteMember = async (tenantId: string, memberId: number): Promise<void> => {
    await apiClient.delete(`/societies/${tenantId}/members/${memberId}`)
  }

  getMyInvitations = async (): Promise<SocietyInvitation[]> => {
    const response = await apiClient.get<SocietyInvitation[]>('/users/me/societies/invitations')
    return response.data
  }

  getMyMemberships = async (): Promise<UserSocietyMembership[]> => {
    const response = await apiClient.get<UserSocietyMembership[]>('/users/me/societies/memberships')
    return response.data
  }

  acceptInvitation = async (membershipId: number): Promise<void> => {
    await apiClient.post(`/users/me/societies/${membershipId}/accept`)
  }

  declineInvitation = async (membershipId: number): Promise<void> => {
    await apiClient.delete(`/users/me/societies/${membershipId}`)
  }

  leaveSociety = async (membershipId: number): Promise<void> => {
    await apiClient.delete(`/users/me/societies/${membershipId}`)
  }

  getJoinCode = async (tenantId: string): Promise<SocietyJoinCode> => {
    const response = await apiClient.get<SocietyJoinCode>(`/societies/${tenantId}/members/join-code`)
    return response.data
  }

  regenerateJoinCode = async (tenantId: string): Promise<SocietyJoinCode> => {
    const response = await apiClient.post<SocietyJoinCode>(`/societies/${tenantId}/members/join-code`)
    return response.data
  }

  joinWithCode = async (code: string): Promise<UserSocietyMembership> => {
    // Suppress the global 404/error toast — callers render their own feedback (or none, on the
    // /join landing flow); the interceptor would otherwise surface a stray "Resource not found".
    const response = await apiClient.post<UserSocietyMembership>('/users/me/societies/join', { code }, { suppressErrorToast: true } as Record<string, unknown>)
    return response.data
  }

  resolveJoinCode = async (code: string): Promise<SocietyJoinCodePreview> => {
    // Invalid/expired codes 404 by design; the landing page renders its own invalid state, so
    // keep the global error toast off this resolve call.
    const response = await apiClient.get<SocietyJoinCodePreview>(`/public/societies/join-codes/${encodeURIComponent(code)}`, { suppressErrorToast: true } as Record<string, unknown>)
    return response.data
  }
}

export const societyService = new SocietyService()
